import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TeamMember } from '@/types/team';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StarIcon from '@/components/icons/StarIcon';

interface CaptainReassignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  players: TeamMember[];
  // Note: this is the TeamMember (TeamMembers collection) `_id`, not the User `_id` (`player.id`).
  // Backend team.members.players[] stores TeamMember ids, so we must send `player._id`.
  onConfirm: (newCaptainMemberId: string) => Promise<void> | void;
  title?: string;
  description?: string;
  isSubmitting?: boolean;
}

const CaptainReassignmentModal = ({
  isOpen,
  onClose,
  players,
  onConfirm,
  title,
  description,
  isSubmitting = false,
}: CaptainReassignmentModalProps) => {
  const { t } = useTranslation('teamsection');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setSelectedPlayerId('');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!selectedPlayerId) return;
    await onConfirm(selectedPlayerId);
    setSelectedPlayerId('');
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setSelectedPlayerId('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:w-full bg-gray-900 border-purple-500/30 text-white max-w-md'>
        <DialogHeader>
          <DialogTitle className='text-purple-300 text-center'>
            {title || t('captainReassignment.title')}
          </DialogTitle>
        </DialogHeader>

        <p className='text-gray-300 text-sm text-center'>
          {description || t('captainReassignment.description')}
        </p>

        <div className='space-y-2 max-h-[45vh] overflow-y-auto'>
          {players.map(player => {
            const isSelected = selectedPlayerId === player._id;
            return (
              <button
                key={player._id}
                type='button'
                onClick={() => setSelectedPlayerId(player._id)}
                disabled={isSubmitting}
                className={`w-full rounded px-4 py-2 flex items-center justify-between transition-colors border ${
                  isSelected
                    ? 'bg-gradient-to-r from-yellow-900/30 to-gray-900/60 border-yellow-400/60 hover:border-yellow-300/90 hover:shadow-[0_0_8px_2px_rgba(234,179,8,0.25)]'
                    : 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-white'
                }`}
              >
                <span className='font-medium'>
                  {player.inGameName || player.name}
                </span>
                {isSelected && (
                  <span
                    className='inline-flex items-center px-2 py-0.5 rounded-full bg-gradient-to-r from-yellow-400/90 to-yellow-500/80 text-yellow-900 text-xs font-semibold shadow-sm border border-yellow-300/60'
                    style={{ letterSpacing: '0.02em' }}
                  >
                    <StarIcon className='w-3.5 h-3.5 me-1 text-yellow-700' />
                    {t('memberActions.captain')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className='flex justify-end gap-2'>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isSubmitting}
            className='bg-gray-700 text-white border-gray-600 hover:bg-gray-600'
          >
            {t('captainReassignment.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedPlayerId || isSubmitting}
            className='bg-[var(--brand-primary)] text-white hover:opacity-90'
          >
            {t('captainReassignment.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CaptainReassignmentModal;
